module Client.AdminEditCategory

open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open Fable.Core.JsInterop
open Fetch
open Fable
open Shared
open Utils
open AdminShared

type Msg =
    | FetchError of exn
    | PageClicked of Domain.Location * ClickAction
    | AddNewCategory
    | SaveCategory
    | LoadedCategoryList of Domain.Location list
    | ConfirmDeleteCategory
    | ConfirmedCategoryDeletion
    | CancelDeletion
    | CategoryDeleted
    | CategorySaved
    | ReturnToCategoryList
    | IdChanged of Slug
    | NameChanged of string

type EditCategoryState =
    {
        Location: Domain.Location
        IsNew: bool
    }

type AdminCategoryAction =
    | Listing of LoadState<unit, Domain.Location list>
    | Editing of LoadState<Domain.Location, EditCategoryState>

type Model = 
    { 
        User: JWT;
        Action: AdminCategoryAction;
        ShowConfirmDelete: bool;
    }

let getAllCategoriesCmd (jwt: JWT) =
    let url = Urls.Categories (TenantRef.get jwt.Account.Tenant) 
    FetchRequest.getWithToken url jwt.Token LoadedCategoryList FetchError

let postSaveCategoryCmd (jwt: JWT) (location: Domain.Location) = 
    let url = Urls.Categories (TenantRef.get jwt.Account.Tenant)
    FetchRequest.postWithToken url location jwt.Token (fun _ -> CategorySaved) FetchError

let deletePageCmd (jwt: JWT) id = 
    let url = Urls.CategoryById (TenantRef.get jwt.Account.Tenant) (Slug.get id)
    FetchRequest.deleteWithToken url () jwt.Token (fun _ -> CategoryDeleted) FetchError

let init user = 
    {
        User = user
        ShowConfirmDelete = false
        Action = Listing (Loading ())
    }, getAllCategoriesCmd user

let private editing model modifier cmd success =
     match model.Action with
        | Editing (Loaded pageState) ->  
           modifier model pageState, (cmd model pageState), (success pageState)
        | _ -> failwith "Invalid action"

let private editingAction model modifier cmd success =
     editing model (fun m pageState -> { m with Action = Editing (Loaded(modifier pageState)) }) cmd success

let private editingActionU model modifier cmd = editingAction model modifier cmd (fun _ -> Unchanged)

let update (msg:Msg) model : Model*Cmd<Msg>* SuccessMessageChange =    
    match msg with
    | SaveCategory -> editingActionU model id (fun m ps -> postSaveCategoryCmd model.User ps.Location)
    | AddNewCategory ->
        match model.Action with
        | Listing (Loaded _) ->  
            let newCategory = 
                { 
                    Location = 
                        {
                            Id = Slug ""
                            Name = ""
                        }
                    IsNew = true
                }
            { model with Action = Editing (Loaded newCategory) }, [], Unchanged
        | _ -> failwith "Invalid action"
    | PageClicked (slug, action) ->
        match action with
        | Edit ->
            { model with Action = Editing (Loaded { Location = slug; IsNew = false }) }, [], Unchanged
        | Delete ->
            { model with Action = Editing (Loaded { Location = slug; IsNew = false }); ShowConfirmDelete = true }, [], Unchanged
    | LoadedCategoryList categories ->
        { model with Action = Listing (Loaded categories) }, [], Unchanged
    | IdChanged slug ->
        editingActionU model (fun ps -> { ps with Location = { ps.Location with Id = slug }}) (fun _ _ -> [])
    | NameChanged name ->
        editingActionU model (fun ps -> { ps with Location = { ps.Location with Name = name }}) (fun _ _ -> [])
    | CancelDeletion -> { model with ShowConfirmDelete = false }, [], Unchanged
    | ConfirmedCategoryDeletion -> 
        editingActionU model id (fun _ x -> deletePageCmd model.User x.Location.Id)
    | CategoryDeleted ->
        editingAction model id (fun _ _ -> Cmd.ofMsg ReturnToCategoryList) (fun x -> Show (sprintf "The page '%s' was deleted." x.Location.Name))
    | ConfirmDeleteCategory -> editing model (fun m _ -> { m with ShowConfirmDelete = true }) (fun _ _ -> []) (fun _ -> Unchanged)
    | CategorySaved ->
        editingAction model id (fun _ _ -> Cmd.ofMsg ReturnToCategoryList) (fun x -> Show (sprintf "Your changes to the category '%s' have been saved." x.Location.Name))
    | ReturnToCategoryList ->
        { model with Action = Listing (Loading()); ShowConfirmDelete = false },getAllCategoriesCmd (model.User),Unchanged
    | FetchError exn ->
        model,[],Unchanged

let view model dispatch =
    match model.Action with
    | Listing loadstate ->
        match loadstate with
        | Loading () -> 
            [
                yield div [ ClassName "valign" ] [
                    yield h2 [ Style [ MarginRight "10px" ] ] [ str "Categories" ] 
                    yield button [ 
                        ClassName "btn animated-background" 
                        HTMLAttr.Type "button"
                    ] [ str "Add new category" ]
                ]
                yield div [] [ str  "There are ? maintained categories" ]
                yield table [ ClassName "table animated-background" ] [
                    thead [] [
                        tr [ ClassName "mask" ] [
                            th [ClassName "mask"] [ str "Category Id" ]
                            th [ClassName "mask"] [ str "Category Display Name" ]
                            th [ClassName "mask"] []
                        ]
                    ]
                    tbody [] [
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [
                                a [ jsVoid ] [ str "Go to" ]
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                                
                        ]
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [
                                a [ jsVoid ] [ str "Go to" ]
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                              
                        ]
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [
                                a [ jsVoid ] [ str "Go to" ]
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                                    
                        ]
                    ]
                ]
            ]
        | Loaded categories ->
            [
                yield div [ ClassName "valign" ] [
                    yield h2 [ Style [ MarginRight "10px" ] ] [ str "Categories" ] 
                    yield button [ 
                        ClassName "btn btn-default" 
                        HTMLAttr.Type "button"
                        OnClick (fun _ -> dispatch AddNewCategory)
                    ] [ str "Add new category" ]
                ]
                match categories.Length with
                | 0 -> 
                    yield p [ ClassName "alert alert-info" ] [ str "You have not yet added any categories. Click the 'Add New' button to add your first." ]
                | n ->
                    yield div [] [ str <| sprintf "There %s %d maintained categor%s." (be n) n (plural "y" n) ]
                    yield table [ ClassName "table" ] [
                        thead [] [
                            tr [] [
                                th [] [ str "Category Id" ]
                                th [] [ str "Category Display Name" ]
                                th [] []
                            ]
                        ]
                        tbody [] [
                            yield! categories |> List.map (fun cat -> 
                                tr [] [ 
                                    td [] [ str <| Slug.get cat.Id ]
                                    td [] [ str cat.Name ]
                                    td [] [ 
                                        a [ Href (sprintf "#areas/%s/pages/%s" (TenantRef.get model.User.Account.Tenant) (Slug.get cat.Id)); Target "_blank" ] [ str "Go to" ]
                                        a [ jsVoid; OnClick (fun _ -> dispatch (PageClicked(cat, Edit))) ] [ str "Edit" ]
                                        a [ jsVoid; OnClick (fun _ -> dispatch (PageClicked(cat, Delete))) ] [ str "Delete" ]
                                    ]                                                                    
                                ])
                        ]
                    ]
                    
            ]
    | Editing loadstate -> 
        [     
            match loadstate with
            | Loaded state ->
                let location = state.Location

                yield form [] [
                    formGroup [
                        yield label [ HtmlFor "categoryId"; ClassName "mandatory" ] [ str "Category Id" ]
                        if state.IsNew then
                            yield textInput "categoryId" "Enter the category id / reference." (Slug >> IdChanged) dispatch (Slug.get location.Id) (Some 200.)
                            if locationContainsSpaces location  then
                                yield div [ ClassName "alert alert-danger"; Role "alert" ] [ str "The category id cannot contain spaces" ]                
                        else
                            yield p [ ClassName "form-control-static" ] [ str <| Slug.get location.Id ]
                    ]
                    formGroup [
                        label [ HtmlFor "name"; ClassName "mandatory" ] [ str "Display Name" ]
                        textInput "name" "Enter the display name." NameChanged dispatch (location.Name) (Some 500.)
                    ]
                ]
                yield footer [] [
                    button [ 
                        ClassName "btn btn-primary"
                        OnClick (fun _ -> dispatch ReturnToCategoryList)
                    ] [ str "Go Back" ]
                    button [ 
                        ClassName "btn btn-success"
                        OnClick (fun _ -> dispatch SaveCategory)
                        Disabled (System.String.IsNullOrWhiteSpace <| Slug.get location.Id || System.String.IsNullOrWhiteSpace location.Name || locationContainsSpaces location )
                    ] [ str "Save the Category" ]           
                    button [ 
                        ClassName "btn btn-danger"
                        OnClick (fun _ -> dispatch ConfirmDeleteCategory)
                    ] [ str "Delete this category" ]
                ]
                match model.ShowConfirmDelete with
                | false -> ()
                | true -> 
                    yield! modal "Confirm Deletion" dispatch CancelDeletion [
                        p [] [ str "Are you sure you wish to delete this category? No pages will be modified, but the menu items will no longer be formatted." ]
                    ] [
                        button [ ClassName "btn btn-danger"; OnClick (fun _ -> dispatch ConfirmedCategoryDeletion) ] [ str "Confirm Deletion" ]
                        button [ ClassName "btn btn-default"; OnClick (fun _ -> dispatch CancelDeletion); Keyboard.onEscape CancelDeletion dispatch] [ str "Cancel" ]
                    ]
            | Loading location -> yield str "Loading..."
        ]
